/**
 * The black box: a pulse the app writes to itself so a freeze can be read
 * afterwards instead of guessed at.
 *
 * An anchor watch left running overnight came back in the morning showing the
 * chart and refusing to answer a tap. The phone was cool and plugged in, the
 * rest of the phone was fine, and by then everything that could have told us
 * why had gone. Three separate plausible causes were found by reading the code,
 * all of them real problems worth fixing, none of them provable. That is not a
 * position to be in twice with a safety alarm.
 *
 * So a timer writes a tick every few seconds, and the ticks survive the app
 * being killed. The one that matters is LAG: how late each tick arrived against
 * when it was due. A main thread that is busy, or thrashing its way through
 * garbage collection, cannot deliver a timer on time, so lag climbing is the
 * app telling us it is drowning. Lag steady and then nothing at all is a
 * different animal: something stopped the thread dead, or Android suspended us.
 *
 * The heap figures come from a Chromium extension that is not in any standard
 * and is missing on plenty of engines, so they are read defensively and go null
 * where they are absent. When they ARE there they settle the memory question in
 * one reading, which no amount of reading the source can.
 *
 * Nothing here writes to disk on the tick path: localStorage is synchronous and
 * a few kilobytes is nothing, whereas the Filesystem plugin is a bridge call,
 * and adding a bridge call every few seconds to diagnose a suspected bridge
 * problem would be its own kind of foolish.
 */

/** One pulse. Deliberately short: this is written many times an hour. */
export interface HeartbeatTick {
  /** Wall clock, ms. */
  t: number;
  /**
   * How late this tick was against its due time, ms. The headline number: a
   * main thread that cannot deliver a timer on time is a main thread in
   * trouble, and this measures exactly that.
   */
  lagMs: number;
  /** Was the app on screen? A backgrounded app is throttled on purpose. */
  visible: boolean;
  /** Age of the last GNSS fix at this tick, ms, or null if there has been none. */
  fixAgeMs: number | null;
  /** Used JS heap, MB, where the engine will say. */
  heapMB: number | null;
  /** The engine's heap ceiling, MB. Used against limit is the number that bites. */
  heapLimitMB: number | null;
  /** Points in the live past track, which is the thing most likely to grow. */
  trackPoints: number;
}

/** How long between pulses. Short enough to see a stall, long enough to be free. */
export const HEARTBEAT_MS = 10_000;

/**
 * How many pulses are kept. Fifty minutes of history at the interval above,
 * which is plenty: what killed the app is in the last few minutes before it
 * stopped, not in the hours of healthy running before that.
 */
export const MAX_TICKS = 300;

export const HEARTBEAT_LS = 'acp.heartbeat.v1';

/** A tick, packed small, because this is serialised on every pulse. */
type PackedTick = [number, number, 0 | 1, number, number, number, number];

function pack(t: HeartbeatTick): PackedTick {
  return [
    t.t,
    Math.round(t.lagMs),
    t.visible ? 1 : 0,
    t.fixAgeMs ?? -1,
    t.heapMB ?? -1,
    t.heapLimitMB ?? -1,
    t.trackPoints,
  ];
}

function unpack(p: PackedTick): HeartbeatTick {
  return {
    t: p[0],
    lagMs: p[1],
    visible: p[2] === 1,
    fixAgeMs: p[3] < 0 ? null : p[3],
    heapMB: p[4] < 0 ? null : p[4],
    heapLimitMB: p[5] < 0 ? null : p[5],
    trackPoints: p[6],
  };
}

/** Add a tick to a ring, dropping the oldest. Pure, so the ring is testable. */
export function pushTick(
  ring: HeartbeatTick[],
  tick: HeartbeatTick,
  max = MAX_TICKS,
): HeartbeatTick[] {
  const next = [...ring, tick];
  if (next.length > max) next.splice(0, next.length - max);
  return next;
}

export function serialiseTicks(ring: HeartbeatTick[]): string {
  return JSON.stringify(ring.map(pack));
}

export function parseTicks(raw: string | null): HeartbeatTick[] {
  if (!raw) return [];
  try {
    const arr = JSON.parse(raw) as PackedTick[];
    if (!Array.isArray(arr)) return [];
    // Every field checked, not just the shape. A stored value can be corrupted
    // by a hard process kill mid-flush, and an entry whose timestamp is not a
    // number would sail through a length check and then throw the moment it met
    // a Date. Dropping a bad entry costs one pulse; letting it through cost the
    // whole app on every launch.
    return arr
      .filter(
        (p) =>
          Array.isArray(p) &&
          p.length >= 7 &&
          p.slice(0, 7).every((v) => typeof v === 'number' && Number.isFinite(v)),
      )
      .map(unpack);
  } catch {
    return [];
  }
}

/** What the previous run's ticks say about how it ended. */
export interface RunVerdict {
  /** 'clean' | 'stalled' | 'starved' | 'unknown' */
  kind: 'clean' | 'stalled' | 'starved' | 'unknown';
  /** A line fit for a bug report. */
  summary: string;
  /** When the last pulse was, or null if there were none. */
  lastTickMs: number | null;
  /** The gap between the last pulse and this app starting, ms. */
  gapMs: number | null;
}

/**
 * A timestamp as HH:MM:SS, or a marker when it is not a time at all.
 *
 * Date.prototype.toISOString THROWS on an invalid date rather than returning
 * something useless, so a single corrupted stamp read back off disk would take
 * out whatever was formatting it. In this module that is the boot path.
 */
function hhmmss(ms: number): string {
  const d = new Date(ms);
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(11, 19) : '??:??:??';
}

/** Anything beyond this and a tick was not merely late, the thread was gone. */
const STALL_MS = 60_000;

/**
 * Read the previous run's pulses and say how it ended.
 *
 * Three outcomes worth telling apart. A run whose last pulse is moments before
 * we started again was closed normally, or killed cleanly by Android, and there
 * is nothing to explain. A run whose lag was climbing before it stopped was
 * struggling, and the heap reading alongside says whether it was memory. A run
 * whose pulses were on time right up to the moment they stopped was not
 * struggling at all: something took the thread away between one pulse and the
 * next, which points somewhere else entirely.
 */
export function diagnoseRun(ticks: HeartbeatTick[], nowMs: number): RunVerdict {
  if (!ticks.length) {
    return { kind: 'unknown', summary: 'no previous run recorded', lastTickMs: null, gapMs: null };
  }
  const last = ticks[ticks.length - 1];
  const gapMs = nowMs - last.t;
  const at = hhmmss(last.t);
  const heap =
    last.heapMB != null && last.heapLimitMB != null
      ? ` heap ${last.heapMB}/${last.heapLimitMB} MB`
      : '';
  const tail = ticks.slice(-6);
  const worstLag = Math.max(...tail.map((x) => x.lagMs));
  const base = `last pulse ${at}Z, ${Math.round(gapMs / 1000)}s before this start,${heap} track ${last.trackPoints} pts, worst lag ${Math.round(worstLag / 1000)}s`;

  // A gap no bigger than a couple of pulses means we were writing right up to
  // the end. Nothing to answer for.
  if (gapMs < HEARTBEAT_MS * 3) {
    return { kind: 'clean', summary: `previous run ended cleanly (${base})`, lastTickMs: last.t, gapMs };
  }
  if (worstLag >= STALL_MS) {
    return {
      kind: 'starved',
      summary: `previous run was STRUGGLING before it stopped (${base})`,
      lastTickMs: last.t,
      gapMs,
    };
  }
  return {
    kind: 'stalled',
    summary: `previous run STOPPED DEAD with no warning (${base})`,
    lastTickMs: last.t,
    gapMs,
  };
}


/**
 * A stretch where the page was frozen: the timer simply did not fire.
 *
 * Not a stall and not a crash. Chromium suspends a backgrounded WebView, and
 * while it is suspended nothing in JavaScript runs at all: no timers, no
 * callbacks, no alarm checks. Fixes carry on arriving from the native location
 * service and queue up, so the track fills itself in when the app comes back
 * and looks untouched, which is why this hid for days. The alarms do not fill
 * in. An anchor drag detected twenty minutes late is not a detection.
 */
export interface FreezePeriod {
  /** Last pulse before it went quiet. */
  fromMs: number;
  /** First pulse after it woke up. */
  toMs: number;
  ms: number;
}

/**
 * Find the stretches where the timer did not fire, as opposed to the app being
 * closed and opened again.
 *
 * The tell is the LAG on the pulse that comes back. A timer that was suspended
 * and resumed knows how late it is, because its due time was set before the
 * freeze, so the lag it reports is very nearly the whole gap. That is exactly
 * what the 06 Aug record showed: a 22.5 minute gap, and the returning pulse
 * owning 22.3 minutes of it as lateness. A fresh start after the app was closed
 * sets a new due time, so its first pulse is on time, and the same gap in the
 * record means something completely different.
 */
export function findFreezes(ticks: HeartbeatTick[]): FreezePeriod[] {
  const out: FreezePeriod[] = [];
  for (let i = 1; i < ticks.length; i++) {
    const a = ticks[i - 1];
    const b = ticks[i];
    const gap = b.t - a.t;
    if (gap < HEARTBEAT_MS * 3) continue;
    // The returning pulse has to own most of the gap as lateness. Below that it
    // was a restart, and calling a restart a freeze would send whoever reads
    // this hunting for a suspension that never happened.
    if (b.lagMs < gap * 0.8) continue;
    out.push({ fromMs: a.t, toMs: b.t, ms: gap });
  }
  return out;
}

/**
 * One line about the freezing, or null when there has not been any.
 *
 * Its own line rather than part of the run verdict, because a run can end
 * perfectly cleanly having been frozen for twenty minutes in the middle, and
 * "ended cleanly" is then a true sentence giving a wrong impression.
 */
export function freezeSummary(ticks: HeartbeatTick[]): string | null {
  const freezes = findFreezes(ticks);
  if (!freezes.length) return null;
  const longest = freezes.reduce((a, b) => (b.ms > a.ms ? b : a));
  const mins = (ms: number) => (ms / 60_000).toFixed(1);
  const total = freezes.reduce((sum, f) => sum + f.ms, 0);
  return (
    `${freezes.length} background FREEZE(s): the page stopped running for ` +
    `${mins(total)} min in total, longest ${mins(longest.ms)} min ending ${hhmmss(longest.toMs)}Z. ` +
    `No alarm in the app is checked while it is frozen.`
  );
}

/**
 * The engine's heap reading, in MB, or nulls where it will not say. Chromium
 * only, and not in any standard, so nothing may lean on it being there.
 */
export function readHeap(
  perf: Performance | undefined = globalThis.performance,
): { heapMB: number | null; heapLimitMB: number | null } {
  const mem = (perf as unknown as { memory?: { usedJSHeapSize?: number; jsHeapSizeLimit?: number } })
    ?.memory;
  const used = mem?.usedJSHeapSize;
  const limit = mem?.jsHeapSizeLimit;
  const mb = (v: number | undefined) =>
    typeof v === 'number' && Number.isFinite(v) ? Math.round(v / 1_048_576) : null;
  return { heapMB: mb(used), heapLimitMB: mb(limit) };
}

/**
 * A few lines of the tail, for a bug report. Oldest first, one per line, short
 * enough that a report is still readable.
 */
export function formatTicks(ticks: HeartbeatTick[], count = 8): string[] {
  return ticks.slice(-count).map((x) => {
    const at = hhmmss(x.t);
    const heap = x.heapMB != null ? ` heap=${x.heapMB}` : '';
    const fix = x.fixAgeMs != null ? ` fix=${Math.round(x.fixAgeMs / 1000)}s` : ' fix=none';
    return `${at} lag=${Math.round(x.lagMs)}ms${x.visible ? '' : ' bg'}${heap}${fix} trk=${x.trackPoints}`;
  });
}
